/**
 * A move's sound plan, read from its data (packages/shared data/skills.ts) and the rules' timing (world.ts): the shape
 * it makes (one blow, a chain of blows, a ring round the caster, a spot on the ground, a dash, a volley, a whirlwind,
 * a warp chain, a heal, a buff…), when each blow lands after the `skill` event, how heavy it is, and a per-move seed
 * that turns the art's family voice into this move's own (docs/research/C0_SOUND.md §3).
 *
 * Plain logic (no WebAudio), shared by the registration (sfx/sounds/moves.ts) and the tests.
 */
import { artOfSkill, COMBAT, SKILLS, type SkillDef } from '@nine/shared';
import { FAMILIES } from './families';

export type MoveShape =
  | 'single'
  | 'multi'
  | 'aoe'
  | 'ground'
  | 'dashForward'
  | 'dashTarget'
  | 'volley'
  | 'spin'
  | 'blink'
  | 'tele'
  | 'heal'
  | 'buff'
  | 'scripted';

/** Volley timing as the rules launch it (world.ts VOLLEY_LAUNCH / VOLLEY_STAGGER). */
const VOLLEY_LAUNCH = 0.5;
const VOLLEY_STAGGER = 0.07;
/** Seconds between the blows of a multi-hit move (world.ts `strike`). */
const HIT_GAP = 0.15;

export interface MovePlan {
  shape: MoveShape;
  /** Seconds after the `skill` event at which each blow (or launch) sounds. */
  strikes: number[];
  /** 0 light, 1 medium, 2 heavy. */
  weight: 0 | 1 | 2;
  /** Status flavours the move carries (its target's and its own). */
  status: string[];
  lifesteal: boolean;
  /** Per-move variation, all from the id's hash: a pentatonic step, brightness, tail length, a variant of the art's motif. */
  seed: number;
  deg: number;
  bright: number;
  tail: number;
  variant: number;
  /** Grace blows before each blow (0–2): ta-ta-TA. */
  flam: number;
  /** Register: 0 as written, 1 a tritone lower, 2 a tritone higher. */
  lift: number;
  /** A monster's size: the whole sound lower for a bear, higher for a bat (1 for the arts). */
  size: number;
  /** Which of the heal/buff arpeggios (sfx/moves/families.ts `bloom`). */
  pattern: number;
}

/** Monsters by size (by the first word of the move's id): bigger is lower. */
const SIZE: Readonly<Record<string, number>> = {
  wolf: 1,
  fox: 1.3,
  leopard: 1.15,
  tiger: 0.8,
  bear: 0.62,
  boar: 0.74,
  bat: 1.5,
  spider: 1.22,
  centipede: 0.9,
  viper: 1.1,
  scorpion: 0.84,
  python: 0.6,
  jiangshi: 0.95,
  bandit: 1,
  assassin: 1.2,
  monk: 0.85,
};

/**
 * Moves that share a motif (an art's, a monster family's), a kind and a shape would differ only in pitch from the hash: within
 * such a group each move takes the next combination of grace blows, register and motif variant instead, so no two of
 * a group are built the same way.
 */
let GROUP_INDEX: Map<string, number> | null = null;
function groupIndexOf(s: SkillDef): number {
  if (!GROUP_INDEX) {
    GROUP_INDEX = new Map();
    const groups = new Map<string, string[]>();
    for (const o of Object.values(SKILLS)) {
      const fid = familyIdOf(o);
      const key = `${FAMILIES[fid]?.motif ?? fid}|${o.kind}|${shapeOf(o)}`;
      (groups.get(key) ?? groups.set(key, []).get(key)!).push(o.id);
    }
    for (const ids of groups.values()) ids.sort().forEach((id, i) => GROUP_INDEX!.set(id, i));
  }
  return GROUP_INDEX.get(s.id) ?? hashId(s.id) % 36;
}

/** FNV-1a: a stable 32-bit hash of a move's id. */
export function hashId(id: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function shapeOf(s: SkillDef): MoveShape {
  if (s.scripted) return 'scripted';
  if (s.kind === 'heal') return 'heal';
  if (s.kind === 'buff') return 'buff';
  if (s.blink) return 'blink';
  if (s.spin) return 'spin';
  if (s.telegraph) return 'tele';
  if (s.volley) return 'volley';
  if (s.dash === 'forward') return 'dashForward';
  if (s.aoe && s.ground) return 'ground';
  if (s.aoe) return 'aoe';
  if (s.dash === 'target') return 'dashTarget';
  if ((s.hits ?? 1) > 1) return 'multi';
  return 'single';
}

export function planOf(s: SkillDef): MovePlan {
  const shape = shapeOf(s);
  const h = hashId(s.id);
  const g = groupIndexOf(s);
  const delay =
    s.kind === 'power' ? COMBAT.strikeDelayPower : s.fx === 'quake' ? COMBAT.strikeDelayLeap : COMBAT.strikeDelay;
  let strikes: number[];
  if (shape === 'volley') strikes = Array.from({ length: s.volley ?? 1 }, (_, k) => VOLLEY_LAUNCH + k * VOLLEY_STAGGER);
  else if (shape === 'blink' || shape === 'spin') strikes = [0.08];
  else if (shape === 'heal' || shape === 'buff') strikes = [];
  else if (shape === 'tele') strikes = [s.telegraph!.delay];
  else if (shape === 'scripted') strikes = [0.12];
  else strikes = Array.from({ length: s.hits ?? 1 }, (_, k) => delay + k * HIT_GAP);
  const weight: 0 | 1 | 2 =
    s.kind === 'power' || (s.kind === 'break' && s.windup >= 0.8) ? 2 : s.kind === 'break' || s.power >= 2 ? 1 : 0;
  const status = [s.applies?.status, s.self?.status, s.onGuardBreak?.status, s.empoweredBy?.status].filter(
    (x): x is string => !!x,
  );
  return {
    shape,
    strikes,
    weight,
    status,
    lifesteal: !!s.lifesteal,
    seed: h,
    deg: (h % 7) - 2,
    bright: 0.82 + ((h >>> 3) % 9) * 0.05,
    tail: 0.8 + ((h >>> 7) % 6) * 0.1,
    variant: (g + (h >>> 11)) % 4,
    lift: g % 3,
    flam: Math.floor(g / 3) % 3,
    size: SIZE[s.id.split('-')[0]!] ?? 1,
    pattern: g % 6,
  };
}

/** The art a move belongs to, or the family a monster's or a boss's move is voiced by. */
export function familyIdOf(s: SkillDef): string {
  const art = artOfSkill(s.id);
  if (art) return art.id;
  const p = s.id.split('-')[0]!;
  if (s.id.startsWith('wb-ape')) return 'ape';
  if (s.id.startsWith('wb-bird')) return 'firebird';
  if (s.id.startsWith('wb-turtle')) return 'turtle';
  if (s.id.startsWith('wb-') || s.id.startsWith('boss-')) return 'demon';
  if (p === 'dg') return 'pirate';
  if (p === 'hidden' || p === 'knife') return 'thrower';
  if (p === 'bandit' || p === 'assassin' || p === 'monk') return 'outlaw';
  if (['wolf', 'bear', 'boar', 'tiger', 'leopard', 'fox'].includes(p)) return 'beast';
  return 'vermin';
}
